/**
 * GuardianshipPage — Brief 5 B6 Guardianship / Curadoria (Sprint Frontend Brief 5 · Tela 2).
 *
 * Lista + detalhe + criar + revogar de relações de representação legal
 * (tutela/curatela/cuidador/temporária/emergência). Tudo tenant-scoped (P1).
 *
 * Padrões: Opção A (capability-driven UI) · D-B defense-in-depth (gate próprio
 * da página). Gate: `legal-capacity-management` (tierMin Regulated).
 *
 * KPIs derivados SOMENTE de campos reais do serializer (R15): total, expirando
 * em ≤6 meses (isExpiringSoon), sem documentação (documentReferenceId === null).
 * O KPI "wards menores" do mockup foi descartado — a API não expõe idade.
 *
 * Backend: módulo adversarial (guardianships.controller). Enums: @lume/shared (ADR-047).
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useMemo, useState } from 'react';

import { CreateGuardianshipModal } from '../features/dpo/guardianship/CreateGuardianshipModal';
import { GuardianshipCard } from '../features/dpo/guardianship/GuardianshipCard';
import { GuardianshipDetailDrawer } from '../features/dpo/guardianship/GuardianshipDetailDrawer';
import { isExpiringSoon } from '../features/dpo/guardianship/types';
import { useCapability } from '../hooks/use-capability';
import { useCreateGuardianship, useGuardianships } from '../hooks/use-guardianships';

import '../features/dpo/guardianship/guardianship.css';

export function GuardianshipPage(): JSX.Element {
  const canManage = useCapability('legal-capacity-management');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canManage) {
    return (
      <div className="g-scope guardianship-page" style={{ padding: 32 }}>
        <CapabilityLockedBanner
          capability="legal-capacity-management"
          tierMin="regulated"
          fallbackMessage="Curadoria / capacidade legal é restrita ao DPO e ao administrador do tenant (pacote Regulated)."
        />
      </div>
    );
  }

  return <GuardianshipPageInner />;
}

function GuardianshipPageInner(): JSX.Element {
  const { data: guardianships, isLoading, isError } = useGuardianships();
  const create = useCreateGuardianship();

  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const kpis = useMemo(() => {
    const total = guardianships.length;
    const expiring = guardianships.filter((g) => isExpiringSoon(g)).length;
    const undocumented = guardianships.filter((g) => g.documentReferenceId === null).length;
    return { total, expiring, undocumented };
  }, [guardianships]);

  function handleSubmit(req: Parameters<typeof create.mutate>[0]): void {
    setCreateError(null);
    create.mutate(req, {
      onSuccess: () => {
        setShowCreate(false);
      },
      onError: (err) => {
        setCreateError(
          err.message !== '' ? err.message : 'Não foi possível registrar a guardianship.',
        );
      },
    });
  }

  return (
    <div className="g-scope guardianship-page">
      <header className="g-pagehead">
        <div className="g-pagehead__row">
          <div>
            <h1 className="g-pagehead__title">Curadoria / capacidade legal</h1>
            <p className="g-pagehead__sub">
              Gestão de representação legal — tutela, curatela, cuidador autorizado, temporária e
              contato de emergência. Documentação rastreável · vigência controlada.
            </p>
          </div>
          <button
            type="button"
            className="g-btn g-btn--primary"
            onClick={() => {
              setCreateError(null);
              setShowCreate(true);
            }}
          >
            + Nova guardianship
          </button>
        </div>

        <div className="g-kpis">
          <div className="g-kpi">
            <span className="g-kpi__label">Total</span>
            <span className="g-kpi__val">{kpis.total}</span>
            <span className="g-kpi__hint">5 tipos de representação</span>
          </div>
          <div className="g-kpi">
            <span className="g-kpi__label">Expirando ≤ 6 meses</span>
            <span className={`g-kpi__val${kpis.expiring > 0 ? ' g-kpi__val--warn' : ''}`}>
              {kpis.expiring}
            </span>
            <span className="g-kpi__hint">renovação preventiva</span>
          </div>
          <div className="g-kpi">
            <span className="g-kpi__label">Sem documentação</span>
            <span className={`g-kpi__val${kpis.undocumented > 0 ? ' g-kpi__val--warn' : ''}`}>
              {kpis.undocumented}
            </span>
            <span className="g-kpi__hint">documentação pendente</span>
          </div>
        </div>
      </header>

      <section className="g-list-section">
        {isLoading ? (
          <p className="g-muted">Carregando guardianships…</p>
        ) : isError ? (
          <p className="g-alert g-alert--error" role="alert">
            Falha ao carregar as guardianships.
          </p>
        ) : guardianships.length === 0 ? (
          <p className="g-muted">Nenhuma guardianship registrada neste tenant.</p>
        ) : (
          <div className="g-grid">
            {guardianships.map((g) => (
              <GuardianshipCard key={g.id} item={g} onOpen={setSelectedId} />
            ))}
          </div>
        )}
      </section>

      {showCreate && (
        <CreateGuardianshipModal
          onClose={() => setShowCreate(false)}
          onSubmit={handleSubmit}
          isSubmitting={create.isPending}
          error={createError}
        />
      )}

      <GuardianshipDetailDrawer guardianshipId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}
